import React, { useState, useEffect, useRef } from "react";
import { Box, Checkbox, Typography, FormControlLabel, Grid, TextField } from "@mui/material";
import AddBoxIcon from '@mui/icons-material/AddBox';
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox';
import "./EntitySelection.css";

const EntitySelection = ({ data, initialSelection, onSelectionChange, groupID }) => {
    const [expandedCountries, setExpandedCountries] = useState([]);
    const [selectedEntities, setSelectedEntities] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const initialized = useRef(false);

    useEffect(() => {
        if (initialized.current || !initialSelection || !initialSelection.Countries) return;

        const newSelectedEntities = initialSelection?.Countries?.map(selCountry => ({
            CountryId: selCountry.CountryId,
            EntityId: selCountry.EntityId
        }));

        setSelectedEntities(newSelectedEntities);
        initialized.current = true;
    }, [initialSelection]);

    useEffect(() => {
        if (!onSelectionChange) return;

        const selectedData = { Countries: selectedEntities };
        onSelectionChange(selectedData);
    }, [selectedEntities, onSelectionChange]);

    const handleEntityCheckbox = (entity, countryId) => {
        if (entity.EntityAdminGroupId !== null && entity.EntityAdminGroupId !== groupID) return;

        setSelectedEntities(prev => {
            const exists = prev.some(item => item.EntityId === entity.EntityId);
            return exists
                ? prev.filter(item => item.EntityId !== entity.EntityId)
                : [...prev, { CountryId: countryId, EntityId: entity.EntityId }];
        });
    };

    const handleCountryCheckbox = (country) => {
        // Filter only entities that belong to the given groupID
        const validEntities = country.Entities
            .filter(entity => entity.EntityAdminGroupId === groupID) // Only select entities mapped to groupID
            .map(entity => ({ CountryId: country.CountryId, EntityId: entity.EntityId }));
    
        if (validEntities.length === 0) return; // If no valid entities, do nothing
    
        const allSelected = validEntities.every(entity =>
            selectedEntities.some(sel => sel.EntityId === entity.EntityId)
        );
    
        setSelectedEntities(prev => allSelected
            ? prev.filter(item => !validEntities.some(entity => entity.EntityId === item.EntityId)) // Remove if already selected
            : [...prev, ...validEntities.filter(entity => !prev.some(item => item.EntityId === entity.EntityId))] // Add only valid entities
        );
    };
    

    const handleCountryToggle = (countryId) => {
        setExpandedCountries(prev => prev.includes(countryId) ? prev.filter(id => id !== countryId) : [...prev, countryId]);
    };



    

    const handleSelectAll = () => {
        const allEntities = data?.flatMap(country =>
            country.Entities
                .filter(entity => entity.EntityAdminGroupId === null || entity.EntityAdminGroupId === groupID) // Only select valid entities
                .map(entity => ({ CountryId: country.CountryId, EntityId: entity.EntityId }))
        );
    
        setSelectedEntities(allEntities);
    };

    const handleDeselectAll = () => {
        setSelectedEntities([]);
    };

    const filteredCountries = data?.filter(country =>
        country.CountryName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        country.Entities.some(entity => entity.Entity.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    return (
        <Box>
            <TextField
                variant="outlined"
                fullWidth
                margin="normal"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search countries or entities..."
            />
            <Box mt={2} sx={{ backgroundColor: "white", padding: "10px", maxHeight: "350px", overflow: "overlay" }}>
                <Box sx={{ margin: "5px 16px", padding: "2px 5px", border: "1px solid lightGray", borderRadius: "5px" }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={selectedEntities.length > 0}
                                indeterminate={selectedEntities.length > 0 && selectedEntities.length < data.flatMap(country => country.Entities).length}
                                onChange={(e) => {
                                    if (e.target.checked) {
                                        handleSelectAll();
                                    } else {
                                        handleDeselectAll();
                                    }
                                }}
                            />
                        }
                        label="All Countries"
                    />

                </Box>

                {filteredCountries?.map((country) => (
                    <Box key={country.CountryId} sx={{ margin: "5px 16px", padding: "2px 5px", border: "1px solid lightGray", borderRadius: "5px" }}>
                        
                        <Box display="flex" alignItems="center" justifyContent="space-between" style={{ cursor: "pointer" }}>
                            <FormControlLabel
                                control={<Checkbox
                                    checked={country.Entities.some(entity => 
                                        selectedEntities.some(sel => sel.EntityId === entity.EntityId)
                                    )}
                                    onChange={() => handleCountryCheckbox(country)}
                                />}
                                label={
                                    <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                                        {country.CountryName}
                                    </Typography>
                                }
                            />
                            <Box onClick={() => handleCountryToggle(country.CountryId)}>
                                {expandedCountries.includes(country.CountryId) ? <IndeterminateCheckBoxIcon /> : <AddBoxIcon />}
                            </Box>
                        </Box>
                        {expandedCountries.includes(country.CountryId) && (
                            <Grid container spacing={1} ml={2} sx={{ display: "block" }}>
                                {country.Entities.filter(entity => entity.Entity.toLowerCase().includes(searchTerm.toLowerCase())).map((entity) => (
                                    <Grid item key={entity.EntityId}>
                                        <FormControlLabel
                                            control={<Checkbox
                                                checked={selectedEntities.some(item => item.EntityId === entity.EntityId)}
                                                onChange={() => handleEntityCheckbox(entity, country.CountryId)}
                                                disabled={entity.EntityAdminGroupId !== null && entity.EntityAdminGroupId !== groupID}
                                            />}
                                            label={
                                                <>
                                                    {entity.Entity} {entity?.EntityAdminGroup !== "No Admin Group" && (
                                                        <span className="spanStyle">Mapped in {entity?.EntityAdminGroup}</span>
                                                    )}
                                                </>
                                            }
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        )}
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

export default EntitySelection;